import { computed, onMounted, ref } from 'vue';
import md5 from 'md5';
import { useApiFieldErrors, useColors, useI18n, useReportMetrics } from '@/composables';
import { useStore } from '@/store';
import { useTableQuery } from '@/composables/useTableQuery';
import { $emitter } from '@/services/emitter';
import {
  postCustomColumnApiRequest,
  putCustomColumnApiRequest,
} from '@/services/api/custom-columns-api';
import { refreshSupportedMetricsApiRequest } from '@/services/api/api-services/report';
import { refreshCustomColumns } from '@/services/api/api-services/load-profile';
import { buildFormulaMetricOptions } from '@/views/reports/report-metrics-runtime';

const braces = ['(', ')'];
const operators = ['+', '-', '*', '/'];
const separatorsReg = new RegExp(`([${['+', '-', '/(', '/)', '*', '/'].join('')}])`, 'g');
const identifier = /^{\d+}$/;
const identifiers = /{(\d+)}/g;

const getBoundaryRegex = (word: string) =>
  new RegExp(word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');

const sortByLength = <T extends { text: string }>(items: T[]) =>
  [...items].sort((itemA, itemB) => itemB.text.length - itemA.text.length);

const checkIsEqual = (str1: string, str2: string): boolean =>
  str1.toLowerCase() === str2.toLowerCase();

type FormulaToken = {
  value: string;
  text: string;
  color: string;
};

type FormState = {
  id: string | null;
  columnName: string;
  description: string;
  formula: FormulaToken[];
  formulaText: string;
  format: string;
  decimals: number;
};

type DialogData = {
  item?: {
    id: string;
    text: string;
    description?: string;
    formula: string;
    format: string;
    decimalPlaces: number;
  };
  inheritMetrics?: Array<{ id: string; text: string }>;
  groupIndex?: number;
};

const defaultFormatTypes = (translate: (key: string) => string) => [
  { id: 'number', name: translate('Number') },
  { id: 'percent', name: translate('Percent') },
  { id: 'currency', name: translate('Currency') },
];

const defaultDecimals = () => [
  { id: 0, name: '0' },
  { id: 1, name: '1' },
  { id: 2, name: '2' },
  { id: 3, name: '3' },
  { id: 4, name: '4' },
];

export const useAddNewColumnDialog = (
  props: { data: DialogData },
  emitClose: () => void,
) => {
  const { t, locale } = useI18n();
  const store = useStore();
  const tableQueryApi = useTableQuery();
  const $colors = useColors();
  const reportMetrics = useReportMetrics();

  const {
    fieldErrors,
    errors,
    errorsControlActive,
    errorsDict,
    clearErrors,
    applyApiErrors,
  } = useApiFieldErrors();

  const formatTypes = defaultFormatTypes(t);
  const decimals = defaultDecimals();

  const defaultForm = (): FormState => ({
    id: null,
    columnName: '',
    description: '',
    formula: [],
    formulaText: '',
    format: formatTypes[0].id,
    decimals: decimals[0].id,
  });

  const form = ref<FormState>(defaultForm());
  const show = ref(false);
  const formulaSelectionStart = ref(0);
  const formulaRef = ref<HTMLTextAreaElement | null>(null);
  const oldHash = ref('');

  const metrics = computed(() =>
    buildFormulaMetricOptions({
      metrics: store.metrics as string[],
      customEvents: tableQueryApi.getTableCustomEvents(),
      reportMetricsName: (name) => reportMetrics.reportMetricsName(name),
    }),
  );

  const sortedColumns = computed(() => sortByLength(metrics.value));

  const activeMetrics = computed(() =>
    metrics.value.filter(({ archived }) => !archived),
  );

  const formulaWithIdentifier = computed(() =>
    sortedColumns.value
      .reduce(
        (accumulator, { text }, index) =>
          accumulator.replace(getBoundaryRegex(text), `{${index}}`),
        form.value.formulaText,
      )
      .split(separatorsReg)
      .filter((token) => token),
  );

  const formula = computed((): FormulaToken[] =>
    formulaWithIdentifier.value.map((token, index) => {
      const value = token.trim();

      if (identifier.test(value)) {
        const column = sortedColumns.value[value.slice(1, -1)];
        return {
          value: column.value,
          text: token.replace(identifiers, (_, val) => sortedColumns.value[val].text),
          color: column.archived ? $colors.tangerine : $colors.green,
        };
      }

      if (
        braces.includes(value) ||
        (operators.includes(value) && !operators.includes(formulaWithIdentifier.value[index - 1]))
      ) {
        return {
          text: token,
          value: token,
          color: $colors.blue,
        };
      }

      if (!Number.isNaN(Number(value))) {
        return {
          text: token,
          value: token,
          color: $colors.violet,
        };
      }

      const text = token.replace(identifiers, (_, val) => sortedColumns.value[val].text);

      return {
        text,
        value: text,
        color: $colors.red,
      };
    }),
  );

  const getError = (field: string) => errorsDict.value[field];

  const getMetricsByValue = (value: string) =>
    metrics.value.reduce(
      (accumulator, { text, value: metricValue }) => {
        accumulator[metricValue] = text;
        return accumulator;
      },
      {} as Record<string, string>,
    )[value];

  const getCurrentHash = () => md5(JSON.stringify(form.value));

  const close = () => {
    emitClose();
  };

  const checkClose = () => {
    if (oldHash.value !== getCurrentHash()) {
      $emitter.emit('open-dialog', {
        name: 'ConfirmDialog',
        data: { onConfirm: () => close() },
      });
    } else {
      close();
    }
  };

  const handleChange = () => {
    form.value.formula = formula.value;
  };

  const formulaBlur = () => {
    if (formulaRef.value) {
      formulaSelectionStart.value = formulaRef.value.selectionStart;
    }
  };

  const addText = (text: string) => {
    form.value.formulaText =
      form.value.formulaText.substring(0, formulaSelectionStart.value) +
      text +
      form.value.formulaText.substring(formulaSelectionStart.value);
    form.value.formula = formula.value;
    formulaSelectionStart.value = formulaSelectionStart.value + text.length;
  };

  const clickChip = ({ text }: { text: string }) => {
    addText(text);
  };

  const getEntityByName = (name: string) =>
    props.data.inheritMetrics?.find(({ text }) => checkIsEqual(text, name));

  const resetErrorsWithControl = () => {
    clearErrors();
  };

  const submit = async () => {
    clearErrors();

    const { id, columnName: name, description, format, decimals: decimalPlaces } = form.value;
    const entity = getEntityByName(name);

    if (entity && entity.id !== id) {
      const errorText = t('{0} is already taken', [t('Name')]);
      fieldErrors.value = { name: [errorText] };
      errors.value = [{ message: errorText }];
      errorsControlActive.value = true;
      return;
    }

    const formulaPayload = sortedColumns.value
      .reduce(
        (accumulator, { value }, index) =>
          accumulator.replaceAll(`{${index}}`, `{${value}}`),
        formulaWithIdentifier.value.join(''),
      );

    const params = {
      ...(id && { id }),
      name,
      formula: formulaPayload,
      format,
      decimalPlaces,
      status: 'active',
      description,
    };

    try {
      const createNewColumnResponse = id
        ? await putCustomColumnApiRequest(params as { id: string } & Record<string, unknown>)
        : await postCustomColumnApiRequest(params);

      await Promise.all([refreshSupportedMetricsApiRequest(), refreshCustomColumns()]);

      $emitter.emit('update-custom-columns', {
        action: id ? 'edit' : 'add',
        item: createNewColumnResponse,
        groupIndex: props.data.groupIndex,
      });
      close();
    } catch (error) {
      applyApiErrors(error, { nested: true });
    }
  };

  onMounted(() => {
    form.value = { ...defaultForm() };
    clearErrors();

    const { item } = props.data;
    if (item) {
      const { id, text: columnName, description, formula: itemFormula, format, decimalPlaces } =
        item;
      form.value.formulaText = itemFormula.replace(/{(.+?)}/g, (_, value) =>
        getMetricsByValue(value),
      );

      form.value = {
        ...form.value,
        id,
        columnName,
        description: description || '',
        format,
        decimals: decimalPlaces,
      };
      handleChange();
    }

    oldHash.value = getCurrentHash();
    show.value = true;
  });

  return {
    t,
    locale,
    $colors,
    show,
    form,
    formatTypes,
    decimals,
    activeMetrics,
    formulaRef,
    errors,
    errorsControlActive,
    getError,
    checkClose,
    handleChange,
    formulaBlur,
    addText,
    clickChip,
    submit,
  };
};
